import { useMemo } from "react";
import { Tabs, Tag, Empty } from "antd";
import ResizableDrawer from "@/components/UI/ResizableDrawer";
import Loader from "@/components/UI/Loader";
import ErrorComponent from "@/components/UI/Error";
import { useFetch } from "@/hooks/FetchHook";
import { formatDateLong } from "@/utils/dateUtils";
import type {
  ProvenanceField,
  ProvenancePayload,
  ProvenanceSource,
} from "./provenance.types";
import { HANDLING_LABELS } from "./provenance.types";

interface ProvenanceDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Dataset key for this screen; the backend resolves it to core tables. */
  datasetName: string;
  /** Screen title, shown so the drawer names what it is describing. */
  title?: string;
}

/** A source's freshness, or an honest blank when the feed reports none. */
function lastSynced(source: ProvenanceSource): string {
  return source.last_synced ? formatDateLong(source.last_synced) : "Not reported";
}

function SourceCard({ source }: { source: ProvenanceSource }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4 mb-3">
      <div className="flex items-center gap-2 mb-3">
        <span className="font-semibold text-sm">
          {source.source_display_name}
        </span>
        {source.vendor && <Tag className="m-0">{source.vendor}</Tag>}
      </div>
      <dl className="grid grid-cols-2 gap-y-1 text-sm">
        <dt className="text-gray-500">Maps to Vivo table</dt>
        <dd className="text-right font-mono text-xs self-center">
          {source.target_table}
        </dd>
        {source.rows_loaded !== null && (
          <>
            <dt className="text-gray-500">Records loaded</dt>
            <dd className="text-right tabular-nums">
              {source.rows_loaded.toLocaleString()}
            </dd>
          </>
        )}
        <dt className="text-gray-500">Last synced</dt>
        <dd className="text-right">{lastSynced(source)}</dd>
        {source.cadence && (
          <>
            <dt className="text-gray-500">Cadence</dt>
            <dd className="text-right">{source.cadence}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

function FieldRow({ field }: { field: ProvenanceField }) {
  const handling = field.handling ?? "derived";
  return (
    <div className="border border-gray-200 rounded-lg px-3 py-2 mb-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-mono text-xs">{field.target_column}</span>
        <Tag className="m-0 shrink-0">
          {HANDLING_LABELS[handling] ?? handling}
        </Tag>
      </div>
      {field.source_field ? (
        <div className="mt-1.5 flex items-baseline gap-2 text-xs">
          <span className="text-gray-500">{field.source_display_name}</span>
          <span className="font-mono">
            {field.source_dataset
              ? `${field.source_dataset}.${field.source_field}`
              : field.source_field}
          </span>
        </div>
      ) : (
        // A column Vivo computes has no source field, and saying so is the
        // honest answer -- not an omission to be filled in later.
        <div className="mt-1.5 text-xs text-gray-500">Derived in Vivo</div>
      )}
      {field.handling_detail && (
        <div className="mt-1 text-xs text-gray-400 font-mono">
          {field.handling_detail}
        </div>
      )}
    </div>
  );
}

export default function ProvenanceDrawer({
  open,
  onClose,
  datasetName,
  title,
}: ProvenanceDrawerProps) {
  const { data, isLoading, error } = useFetch({
    endpointSuffix: `provenance/?dataset=${encodeURIComponent(datasetName)}`,
    // Only fetch once the drawer is actually opened: provenance is a
    // question users ask occasionally, not something every screen load pays for.
    enabled: open,
  });

  const payload = data as ProvenancePayload | null;

  const fieldsByTable = useMemo(() => {
    const groups = new Map<string, ProvenanceField[]>();
    for (const field of payload?.fields ?? []) {
      const existing = groups.get(field.target_table);
      if (existing) existing.push(field);
      else groups.set(field.target_table, [field]);
    }
    return groups;
  }, [payload]);

  const sources = payload?.sources ?? [];
  const sourceNames = [
    ...new Set(sources.map((source) => source.source_display_name)),
  ];

  const body = () => {
    if (isLoading) return <Loader />;
    if (error) return <ErrorComponent />;
    if (!sources.length && !fieldsByTable.size) {
      return (
        <Empty
          description="No lineage recorded for this screen's data yet."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <Tabs
        items={[
          {
            key: "sources",
            label: "Sources",
            children: (
              <div>
                {sources.map((source) => (
                  <SourceCard
                    key={`${source.target_table}-${source.source_system}-${source.source_dataset}`}
                    source={source}
                  />
                ))}
              </div>
            ),
          },
          {
            key: "fields",
            label: "Fields",
            children: (
              <div>
                {[...fieldsByTable.entries()].map(([table, fields]) => (
                  <div key={table} className="mb-4">
                    {fieldsByTable.size > 1 && (
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                        {table}
                      </div>
                    )}
                    {fields.map((field) => (
                      <FieldRow
                        key={`${field.target_table}.${field.target_column}`}
                        field={field}
                      />
                    ))}
                  </div>
                ))}
              </div>
            ),
          },
        ]}
      />
    );
  };

  return (
    <ResizableDrawer
      headerTitle="Data Provenance"
      headersArray={
        sourceNames.length
          ? [
              {
                key: "sources",
                value: `${title ? `${title} · ` : ""}${sourceNames.join(" · ")}`,
              },
            ]
          : undefined
      }
      placement="right"
      open={open}
      onClose={onClose}
      defaultWidth={560}
      data-testid="provenance-drawer"
    >
      {body()}
    </ResizableDrawer>
  );
}
